import React from 'react';
import PropTypes from 'prop-types';

/**
 * Reusable pagination component for tables.
 * Displays page navigation, per-page selector, and record count.
 *
 * @param {Object} props
 * @param {number} props.totalItems - Total number of items
 * @param {number} props.currentPage - Current page (1-indexed)
 * @param {number} props.perPage - Items per page
 * @param {Function} props.onPageChange - Called with new page number
 * @param {Function} props.onPerPageChange - Called with new per-page value
 * @param {number[]} [props.perPageOptions=[10,25,50]] - Available per-page options
 * @returns {JSX.Element}
 *
 * @example
 * <Pagination
 *   totalItems={150}
 *   currentPage={2}
 *   perPage={25}
 *   onPageChange={(page) => setCurrentPage(page)}
 *   onPerPageChange={(perPage) => setPerPage(perPage)}
 * />
 */
function Pagination({
  totalItems,
  currentPage,
  perPage,
  onPageChange,
  onPerPageChange,
  perPageOptions = [10, 25, 50]
}) {
  const totalPages = Math.ceil(totalItems / perPage);
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const endItem = Math.min(currentPage * perPage, totalItems);

  /**
   * Generate array of page numbers to display with ellipsis logic.
   * Shows: first page, last page, current +/- 1, with ellipsis between gaps.
   *
   * @returns {(number|string)[]} Array of page numbers or 'ellipsis' markers
   */
  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = [];
    pages.push(1);

    if (currentPage > 3) {
      pages.push('ellipsis-start');
    }

    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - 2) {
      pages.push('ellipsis-end');
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="pagination">
      <div className="pagination__info">
        Showing {startItem}-{endItem} of {totalItems}
      </div>

      <div className="pagination__controls">
        <button
          className="btn btn-ghost btn-sm pagination__btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Previous page"
        >
          Previous
        </button>

        {pageNumbers.map((page, index) => {
          if (typeof page === 'string') {
            return (
              <span key={page} className="pagination__ellipsis">
                &hellip;
              </span>
            );
          }
          return (
            <button
              key={page}
              className={`btn btn-sm ${page === currentPage ? 'btn-primary pagination__btn--active' : 'btn-ghost'} pagination__btn`}
              onClick={() => onPageChange(page)}
              aria-label={`Page ${page}`}
              aria-current={page === currentPage ? 'page' : undefined}
            >
              {page}
            </button>
          );
        })}

        <button
          className="btn btn-ghost btn-sm pagination__btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Next page"
        >
          Next
        </button>
      </div>

      <div className="pagination__per-page">
        <label htmlFor="per-page-select">Per page:</label>
        <select
          id="per-page-select"
          value={perPage}
          onChange={(e) => onPerPageChange(Number(e.target.value))}
          aria-label="Items per page"
        >
          {perPageOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

Pagination.propTypes = {
  totalItems: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  perPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPerPageChange: PropTypes.func.isRequired,
  perPageOptions: PropTypes.arrayOf(PropTypes.number),
};

export default Pagination;
